import { useState } from "react"

import { useTranslation } from "react-i18next"

import { CirclePlus, Trash } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"

import { useAddNewLanguage } from "../../ai-translator/hooks/use-add-new-language"
import { useChooseTranslateLanguage } from "../../ai-translator/hooks/use-choose-translate-language"

interface ChooseTranslationLanguagesMultiSelectProps {
  label?: string
  selectedLanguages: string[]
  setSelectedLanguages: (languages: string[]) => void
}

export function ChooseTranslationLanguagesMultiSelect({
  label,
  selectedLanguages,
  setSelectedLanguages,
}: ChooseTranslationLanguagesMultiSelectProps) {
  const { t } = useTranslation()

  const {
    availableLanguages,
    showAddLanguageDialog,
    setShowAddLanguageDialog,
    handleDeleteLanguage,
  } = useChooseTranslateLanguage()

  const [isOpen, setIsOpen] = useState(false)
  const { onAddNewLanguage, form } = useAddNewLanguage()

  // Function to handle toggling a language
  const handleLanguageToggle = (language: string) => {
    if (language === "add_new") {
      setShowAddLanguageDialog(true)
      return
    }

    if (selectedLanguages.includes(language)) {
      // Remove language if already selected
      setSelectedLanguages(selectedLanguages.filter((lang: string) => lang !== language))
    } else {
      // Add language if not selected
      setSelectedLanguages([...selectedLanguages, language])
    }
  }

  // Format selected languages for display
  const getSelectedLanguagesDisplay = () => {
    if (selectedLanguages.length === 0) return t("textinputdropdown.select")
    if (selectedLanguages.length === 1) return selectedLanguages[0]
    return `${selectedLanguages.length} languages selected`
  }

  return (
    <section className="flex w-full flex-col gap-1">
      <Label>{label || t("pages.translate.translate_to")}</Label>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={isOpen}
            className="w-full justify-between"
            role="combobox"
            variant="outline"
          >
            <span>{getSelectedLanguagesDisplay()}</span>
            {selectedLanguages.length > 0 && (
              <div className="mr-2 flex flex-wrap gap-1">
                {selectedLanguages.slice(0, 2).map((lang: string) => (
                  <Badge key={lang} className="mr-1" variant="secondary">
                    {lang}
                  </Badge>
                ))}
                {selectedLanguages.length > 2 && (
                  <Badge variant="secondary">+{selectedLanguages.length - 2}</Badge>
                )}
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[200px] p-0">
          <div className="max-h-[300px] overflow-auto p-1">
            {availableLanguages.map((language) => (
              <div
                key={language}
                className="hover:bg-muted flex cursor-pointer items-center justify-between space-x-2 rounded-sm p-2"
                role="presentation"
                // Mouse convenience only: keyboard users toggle via the Checkbox.
                onClick={(e) => {
                  // Checkbox and its label toggle through onCheckedChange; ignore their clicks here.
                  if ((e.target as Element).closest("label, [role='checkbox']")) return
                  handleLanguageToggle(language)
                }}
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedLanguages.includes(language)}
                    id={`lang-${language}`}
                    onCheckedChange={() => handleLanguageToggle(language)}
                  />
                  <label className="cursor-pointer text-sm" htmlFor={`lang-${language}`}>
                    {language}
                  </label>
                </div>
                <Button
                  aria-label={t("pages.video_insight.remove_language")}
                  className="h-6 w-6"
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteLanguage(language)
                    setSelectedLanguages(
                      selectedLanguages.filter((lang: string) => lang !== language)
                    )
                  }}
                >
                  <Trash className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Separator className="my-1" />
            <button
              className="hover:bg-muted focus-visible:ring-ring flex w-full cursor-pointer items-center space-x-2 rounded-sm p-2 text-left focus-visible:ring-1"
              type="button"
              onClick={() => {
                setIsOpen(false)
                setShowAddLanguageDialog(true)
              }}
            >
              <CirclePlus className="mr-2 size-4" />
              <span className="text-sm">{t("textinputdropdown.add_new")}</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={showAddLanguageDialog} onOpenChange={setShowAddLanguageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("textinputdropdown.title")}</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              className="space-y-4"
              onSubmit={form.handleSubmit((values) => {
                onAddNewLanguage(values)
                // Add the new language to selected languages
                const newLanguage = values.language
                if (newLanguage && !selectedLanguages.includes(newLanguage)) {
                  setSelectedLanguages([...selectedLanguages, newLanguage])
                }
              })}
            >
              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("textinputdropdown.language")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("textinputdropdown.placeholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">{t("textinputdropdown.add")}</Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
